import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Download, Star, Sparkles, CheckCircle, AlertTriangle,
  Calendar, User, Loader2, ChevronRight
} from 'lucide-react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Animated Score Meter Component
const AnimatedScoreMeter = ({ score, label, delay = 0, isDecline = false, animationEnabled = true }) => {
  const [displayScore, setDisplayScore] = useState(0);
  const [animationComplete, setAnimationComplete] = useState(false);
  const controls = useAnimation();

  useEffect(() => {
    if (!animationEnabled) {
      setDisplayScore(score);
      setAnimationComplete(true);
      return;
    }

    const timer = setTimeout(() => {
      // Animate the score counting up
      let current = 0;
      const duration = 1500;
      const steps = 60;
      const increment = score / steps;
      const stepDuration = duration / steps;

      const interval = setInterval(() => {
        current += increment;
        if (current >= score) {
          setDisplayScore(score);
          clearInterval(interval);
          setAnimationComplete(true);
        } else {
          setDisplayScore(Math.round(current));
        }
      }, stepDuration);
    }, delay);

    return () => clearTimeout(timer);
  }, [score, delay, animationEnabled]);

  const getScoreColor = (s) => {
    if (s <= 40) return 'text-red-500';
    if (s <= 65) return 'text-amber-500';
    if (s <= 85) return 'text-green-500';
    return 'text-emerald-500';
  };

  const getScoreLabel = (s) => {
    if (s <= 40) return 'Restoration Recommended';
    if (s <= 65) return 'Improvement In Progress';
    if (s <= 85) return 'Good Condition';
    return 'Excellent Condition';
  };

  return (
    <motion.div
      initial={animationEnabled ? { opacity: 0, y: 20 } : { opacity: 1, y: 0 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: delay / 1000, duration: 0.5, ease: "easeOut" }}
      className={`relative text-center p-6 rounded-2xl bg-gradient-to-br from-zinc-50 to-white border ${
        isDecline && animationComplete ? 'border-red-200' : 'border-zinc-200'
      }`}
    >
      {/* Decline pulse effect */}
      {isDecline && animationComplete && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.3, 0] }}
          transition={{ duration: 1.5, ease: "easeInOut" }}
          className="absolute inset-0 bg-red-500 rounded-2xl"
        />
      )}
      
      <p className="text-sm text-zinc-500 font-medium mb-2">{label}</p>
      <div className={`text-6xl font-bold ${getScoreColor(displayScore)} transition-colors`}>
        {Math.round(displayScore)}
      </div>
      <p className="text-sm text-zinc-600 mt-2">{getScoreLabel(displayScore)}</p>
    </motion.div>
  );
};

// Category Breakdown Card
const CategoryCard = ({ data, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: delay / 1000, duration: 0.4, ease: "easeOut" }}
      className="bg-white rounded-xl p-4 border border-zinc-100"
    >
      <div className="flex justify-between items-center">
        <span className="font-medium text-zinc-800">{data.label}</span>
        <div className="flex items-center gap-2">
          <div className="flex">
            {[1,2,3,4,5].map((n) => (
              <Star
                key={n}
                className={`h-4 w-4 ${n <= data.rating ? 'text-[#F56F05] fill-[#F56F05]' : 'text-zinc-200'}`}
              />
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Star Rating Selector for Feedback
const StarRating = ({ value, onChange }) => {
  const [hover, setHover] = useState(0);
  
  return (
    <div className="flex gap-2 justify-center">
      {[1,2,3,4,5].map((rating) => (
        <button
          key={rating}
          type="button"
          onClick={() => onChange(rating)}
          onMouseEnter={() => setHover(rating)}
          onMouseLeave={() => setHover(0)}
          className="transition-transform hover:scale-110"
          data-testid={`feedback-star-${rating}`}
        >
          <Star
            className={`h-10 w-10 transition-colors ${
              rating <= (hover || value)
                ? 'text-[#F56F05] fill-[#F56F05]'
                : 'text-zinc-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const ClientReportPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [animationPlayed, setAnimationPlayed] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [systemSettings, setSystemSettings] = useState({ report_animation_enabled: true });

  const fetchData = useCallback(async () => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const reportRes = await axios.get(`${API_URL}/api/reports/${reportId}`, { headers });
      setReport(reportRes.data);
      
      // Mark animation as played
      sessionStorage.setItem(`report_animation_${reportId}`, 'true');
    } catch (err) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [reportId, token]);

  useEffect(() => {
    fetchData();
    // Check if animation was already played this session
    const played = sessionStorage.getItem(`report_animation_${reportId}`);
    if (played) setAnimationPlayed(true);
  }, [fetchData, reportId]);

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/reports/${reportId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `gleam-guru-report-${reportId.slice(0,8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  const handleSubmitFeedback = async () => {
    if (feedbackRating === 0) {
      toast.error('Please select a rating');
      return;
    }
    
    setSubmittingFeedback(true);
    try {
      const res = await axios.post(`${API_URL}/api/reports/${reportId}/feedback`, {
        report_id: reportId,
        star_rating: feedbackRating,
        feedback_text: feedbackText || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setFeedbackSubmitted(true);
      
      // Redirect to Google if 4-5 stars
      if (res.data.redirect_to_google && res.data.google_review_url) {
        toast.success('Thank you! Redirecting to Google Reviews...');
        setTimeout(() => {
          window.open(res.data.google_review_url, '_blank');
        }, 1500);
      } else {
        toast.success('Thank you for your feedback!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-zinc-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-zinc-100 flex items-center justify-center">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-zinc-500">Report not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const enableAnimation = systemSettings.report_animation_enabled && !animationPlayed;

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-zinc-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-zinc-200 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/portal/reports')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> My Reports
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadPDF} data-testid="download-pdf-btn">
            <Download className="h-4 w-4 mr-2" /> Download PDF
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        {/* Title */}
        <motion.div
          initial={enableAnimation ? { opacity: 0, y: -20 } : { opacity: 1, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <h1 className="text-3xl font-bold text-zinc-900">Performance Report</h1>
          <p className="text-zinc-600 mt-2">
            {report.service_name} • {new Date(report.service_date).toLocaleDateString('en-US', { 
              weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
            })}
          </p>
          <div className="flex items-center justify-center gap-2 mt-2 text-sm text-zinc-500">
            <User className="h-4 w-4" />
            Performed by {report.cleaner_name}
          </div>
          {report.qc_verified && (
            <Badge className="mt-3 bg-green-100 text-green-700 hover:bg-green-100">
              <CheckCircle className="h-3 w-3 mr-1" /> QC Verified
            </Badge>
          )}
        </motion.div>

        {/* Score Meters */}
        <div className={`grid gap-6 ${report.before_score ? 'md:grid-cols-2' : 'md:grid-cols-1'}`}>
          {report.before_score && (
            <AnimatedScoreMeter
              score={report.before_score}
              label="BEFORE"
              delay={enableAnimation ? 300 : 0}
              animationEnabled={enableAnimation}
            />
          )}
          <AnimatedScoreMeter
            score={report.after_score}
            label="AFTER"
            delay={enableAnimation ? (report.before_score ? 800 : 300) : 0}
            isDecline={report.condition_decline}
            animationEnabled={enableAnimation}
          />
        </div>

        {/* Improvement Delta */}
        {report.improvement_delta !== null && (
          <motion.div
            initial={enableAnimation ? { opacity: 0, scale: 0.9 } : { opacity: 1, scale: 1 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: enableAnimation ? 1.5 : 0, duration: 0.5 }}
            className="text-center"
          >
            <div className={`inline-flex items-center gap-2 px-6 py-3 rounded-full ${
              report.improvement_delta >= 0 ? 'bg-green-100' : 'bg-red-100'
            }`}>
              <span className={`text-3xl font-bold ${
                report.improvement_delta >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {report.improvement_delta >= 0 ? '+' : ''}{Math.round(report.improvement_delta)}
              </span>
              <span className={`text-sm font-medium ${
                report.improvement_delta >= 0 ? 'text-green-700' : 'text-red-700'
              }`}>
                Point {report.improvement_delta >= 0 ? 'Improvement' : 'Change'}
              </span>
            </div>
            
            {report.improvement_badge && (
              <motion.div
                initial={enableAnimation ? { opacity: 0, y: 10 } : { opacity: 1, y: 0 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: enableAnimation ? 1.8 : 0 }}
                className="mt-4"
              >
                <Badge className="bg-gradient-to-r from-[#F56F05] to-orange-400 text-white px-4 py-2 text-sm">
                  <Sparkles className="h-4 w-4 mr-2" />
                  {report.improvement_badge}
                </Badge>
              </motion.div>
            )}
          </motion.div>
        )}

        {/* Condition Decline Warning */}
        {report.condition_decline && (
          <motion.div
            initial={enableAnimation ? { opacity: 0, y: 20 } : { opacity: 1, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: enableAnimation ? 2 : 0 }}
            className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-2xl p-6"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-red-800 text-lg">Condition Change Detected</h3>
                <p className="text-red-700 mt-2">
                  We detected a noticeable shift in your home's condition since your last visit. 
                  A Restoration Upgrade may help restore optimal condition.
                </p>
                <Button
                  onClick={() => navigate('/booking?service=deep_reset')}
                  className="mt-4 bg-[#F56F05] hover:bg-[#C2410C]"
                  data-testid="schedule-restoration-btn"
                >
                  Schedule a Restoration Upgrade
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Category Breakdown */}
        <motion.div
          initial={enableAnimation ? { opacity: 0 } : { opacity: 1 }}
          animate={{ opacity: 1 }}
          transition={{ delay: enableAnimation ? 2.2 : 0 }}
        >
          <Card>
            <CardHeader>
              <CardTitle>Category Breakdown</CardTitle>
              <CardDescription>After-clean condition ratings by category</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {report.after_breakdown && Object.entries(report.after_breakdown).map(([key, data], idx) => (
                <CategoryCard
                  key={key}
                  data={data}
                  delay={enableAnimation ? 2400 + (idx * 100) : 0}
                />
              ))}
            </CardContent>
          </Card>
        </motion.div>

        {/* Photos */}
        <motion.div
          initial={enableAnimation ? { opacity: 0 } : { opacity: 1 }}
          animate={{ opacity: 1 }}
          transition={{ delay: enableAnimation ? 3 : 0 }}
        >
          <Card>
            <CardHeader>
              <CardTitle>Photos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                {report.before_overview_photo && (
                  <div>
                    <p className="text-sm font-medium text-zinc-600 mb-2">Before</p>
                    <img
                      src={report.before_overview_photo}
                      alt="Before"
                      className="rounded-xl w-full h-48 object-cover"
                    />
                  </div>
                )}
                {report.after_overview_photo && (
                  <div>
                    <p className="text-sm font-medium text-zinc-600 mb-2">After</p>
                    <img
                      src={report.after_overview_photo}
                      alt="After"
                      className="rounded-xl w-full h-48 object-cover"
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Trajectory (3+ visits) */}
        {report.trajectory && report.trajectory.length >= 3 && (
          <motion.div
            initial={enableAnimation ? { opacity: 0 } : { opacity: 1 }}
            animate={{ opacity: 1 }}
            transition={{ delay: enableAnimation ? 3.2 : 0 }}
          >
            <Card>
              <CardHeader>
                <CardTitle>Your Progress Journey</CardTitle>
                <CardDescription>Condition improvement since you started with us</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {report.trajectory.map((visit) => (
                    <div key={visit.visit_number} className="flex items-center gap-4">
                      <div className="w-20 text-sm text-zinc-500">Visit {visit.visit_number}</div>
                      <div className="flex-1 h-8 bg-zinc-100 rounded-full overflow-hidden relative">
                        {visit.before_score && (
                          <div
                            className="absolute h-full bg-zinc-300 rounded-full"
                            style={{ width: `${visit.before_score}%` }}
                          />
                        )}
                        <div
                          className="absolute h-full bg-gradient-to-r from-[#F56F05] to-orange-400 rounded-full transition-all"
                          style={{ width: `${visit.after_score}%` }}
                        />
                      </div>
                      <div className="w-16 text-right font-semibold text-[#F56F05]">
                        {Math.round(visit.after_score)}
                      </div>
                    </div>
                  ))}
                </div>
                {report.total_improvement !== null && (
                  <div className="mt-6 text-center p-4 bg-green-50 rounded-xl">
                    <p className="text-green-800 font-semibold">
                      Total Improvement Since Onboarding: 
                      <span className="text-2xl ml-2 text-green-600">+{Math.round(report.total_improvement)}</span>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Feedback Section */}
        <motion.div
          initial={enableAnimation ? { opacity: 0 } : { opacity: 1 }}
          animate={{ opacity: 1 }}
          transition={{ delay: enableAnimation ? 3.5 : 0 }}
        >
          <Card className="border-2 border-dashed border-zinc-200">
            <CardHeader className="text-center">
              <CardTitle>How Did We Do?</CardTitle>
              <CardDescription>Your feedback helps us improve</CardDescription>
            </CardHeader>
            <CardContent>
              {feedbackSubmitted ? (
                <div className="text-center py-6">
                  <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-3" />
                  <p className="font-semibold text-zinc-900">Thank You!</p>
                  <p className="text-sm text-zinc-600 mt-1">Your feedback has been submitted.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  <StarRating value={feedbackRating} onChange={setFeedbackRating} />
                  
                  {feedbackRating > 0 && feedbackRating <= 3 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                    >
                      <textarea
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        placeholder="We'd love to hear how we can improve..."
                        className="w-full p-4 border border-zinc-200 rounded-xl resize-none focus:ring-2 focus:ring-[#F56F05] focus:border-transparent"
                        rows={3}
                        data-testid="feedback-text"
                      />
                    </motion.div>
                  )}
                  
                  <Button
                    onClick={handleSubmitFeedback}
                    disabled={submittingFeedback || feedbackRating === 0}
                    className="w-full bg-[#F56F05] hover:bg-[#C2410C]"
                    data-testid="submit-feedback-btn"
                  >
                    {submittingFeedback ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : null}
                    Submit Feedback
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default ClientReportPage;
